import React from 'react';
import ActivityIcon from 'components/atoms/icons/activityIcon';
import AllergyIcon from 'components/atoms/icons/allergyIcon';
import AverageIcon from 'components/atoms/icons/averageIcon';
import BannerProfileIcon from 'components/atoms/icons/bannerProfileIcon';
import ClinicalActivityIcon from 'components/atoms/icons/clinicalActivityIcon';
import ControlPanelIcon from 'components/atoms/icons/controlPanelIcon';
import GeneralCommentsIcon from 'components/atoms/icons/generalCommentsIcon';
import GraphsIcon from 'components/atoms/icons/graphsIcon';
import MedicalHistoryIcon from 'components/atoms/icons/medicalHistoryIcon';
import MedicationIcon from 'components/atoms/icons/medicationIcon';
import PatientAppointment from 'components/atoms/icons/patientAppointmentIcon';
import PatientGoalsIcon from 'components/atoms/icons/patientGoalsIcon';
import QuestionnaireIcon from 'components/atoms/icons/questionnaireIcon';
import UserProfileIcon from 'components/atoms/icons/userProfileIcon';
import MedicalHistoryCombineIcon from 'components/atoms/icons/rpm-ccm-medical-history.svg?react';
import ClinicalActivityCombineIcon from 'components/atoms/icons/clinical-activity.svg?react';
import UnactionedIcon from 'components/atoms/icons/unactioned-activity.svg?react';
import ActionedIcon from 'components/atoms/icons/actioned-activity.svg?react';
import ServicesIcon from 'components/atoms/icons/services.svg?react';
import InteractionTimeIcon from 'components/atoms/icons/time.svg?react';
import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'utils/deviceHelper';
import { makeid } from 'utils/helpers/makeid';
import useUser from 'utils/useUser';
import { isPatient } from 'utils/userTypeHelper';
import IcdCodeIcon from 'components/atoms/icons/icd-code';

import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface ILink {
  key: string;
  title: string;
  to: string;
  dataCy: string;
  icon: React.ReactNode;
  render: boolean;
  marked?: boolean;
  getColor?: (activityCount?: IRpmActivityCountResponse) => string;
  iconColor?: string;
}

export const useMiniNav = () => {
  const { userType, deviceType, isFullyManagedAgency, isAgencyDoctor } =
    useUser();
  const { t } = useTranslation();

  const isFullyManagedAgencyAndAgencyDoctor =
    isFullyManagedAgency && isAgencyDoctor;

  const linkToBanner = isMobile(deviceType)
    ? '#patient-banner-mob'
    : '#patient-banner';

  const getIndicatorColor = (activityCount?: IRpmActivityCountResponse) => {
    if (!activityCount) return '';

    if (activityCount.critical > 0) return 'red';
    if (activityCount.risk > 0) return 'yellow';
    if (activityCount.normal > 0) return 'green';

    return '';
  };
  const getCriticalIndicatorColor = (
    activityCount?: IRpmActivityCountResponse,
  ) => {
    if (!activityCount) return '';
    if (activityCount.critical > 0) return 'red';
    return '';
  };

  const getNormalAndAtRiskIndicatorColor = (
    activityCount?: IRpmActivityCountResponse,
  ) => {
    if (!activityCount) return '';
    if (activityCount.risk > 0) return 'yellow';
    if (activityCount.normal > 0) return 'green';

    return '';
  };

  const generateLinksWithIndicators = (
    linksList: Record<ModuleType, ILink[]>,
    module: ModuleType,
    activityCount?: IRpmActivityCountResponse,
  ): ILink[] => {
    const links = linksList[module as keyof typeof linksList];

    if (!links) {
      return [];
    }

    if (!activityCount) return links;

    return links.map((link) => {
      if (link.getColor) {
        return {
          ...link,
          iconColor: link.getColor(activityCount),
        };
      }
      return link;
    });
  };

  const linksList: Record<ModuleType, ILink[]> = {
    [ModuleType.RPM]: [
      {
        key: makeid(5),
        title: t('labels.patientBanner'),
        to: linkToBanner,
        dataCy: 'sidebar-patient-banner',
        icon: <UserProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.controlPanel'),
        to: '#control-panel',
        dataCy: 'sidebar-control-panel',
        icon: <ControlPanelIcon />,
        render: !isPatient(userType),
      },
      {
        key: makeid(5),
        title: t('labels.average'),
        to: '#average-metrics',
        dataCy: 'sidebar-average-metrics',
        icon: <AverageIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.graphs'),
        to: '#graphs',
        dataCy: 'sidebar-graphs',
        icon: <GraphsIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.activity'),
        to: '#activity',
        dataCy: 'sidebar-activity',
        icon: <ActivityIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        icon: <MedicationIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.clinicalActivity'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        icon: <ClinicalActivityIcon />,
        render: !isPatient(userType),
      },
      {
        key: makeid(5),
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        icon: <BannerProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        icon: <GeneralCommentsIcon />,
        render: !isPatient(userType),
      },
    ].filter(({ render }) => render),

    [ModuleType.CCM]: [
      {
        key: makeid(5),
        title: t('labels.patientBanner'),
        to: linkToBanner,
        dataCy: 'sidebar-patient-banner',
        icon: <UserProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.controlPanel'),
        to: '#control-panel',
        dataCy: 'sidebar-control-panel',
        icon: <ControlPanelIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.appointment'),
        to: '#appointment-details',
        dataCy: 'sidebar-appointment-details',
        icon: <PatientAppointment />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.questionnaire'),
        to: '#questionnaire',
        dataCy: 'sidebar-questionnaire',
        icon: <QuestionnaireIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.medicalHistory'),
        to: '#medical-history',
        dataCy: 'sidebar-medical-history',
        icon: <MedicalHistoryIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.allergyLog'),
        to: '#allergy-history',
        dataCy: 'sidebar-allergy-history',
        icon: <AllergyIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.clinicalActivity'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        icon: <ClinicalActivityIcon />,
        render: !isPatient(userType),
      },
      {
        key: makeid(5),
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        icon: <MedicationIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.patientGoals'),
        to: '#patient-goals',
        dataCy: 'sidebar-patient-goals',
        icon: <PatientGoalsIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        icon: <BannerProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        icon: <GeneralCommentsIcon />,
        render: !isPatient(userType),
      },
    ].filter(({ render }) => render),

    [ModuleType.ADMIN]: [
      {
        key: makeid(5),
        title: t('labels.patientBanner'),
        to: linkToBanner,
        dataCy: 'sidebar-patient-banner',
        icon: <UserProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.controlPanel'),
        to: '#control-panel',
        dataCy: 'sidebar-control-panel',
        icon: <ControlPanelIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        icon: <BannerProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        icon: <GeneralCommentsIcon />,
        render: true,
      },
    ].filter(({ render }) => render),

    [ModuleType.RPMCCM]: [
      {
        key: makeid(5),
        title: t('labels.patientBanner'),
        to: linkToBanner,
        dataCy: 'sidebar-patient-banner',
        icon: <UserProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('controls.interactiveTime'),
        to: '#interaction-time',
        dataCy: 'sidebar-interaction-time',
        icon: <InteractionTimeIcon width={26} height={26} />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.controlPanel'),
        to: '#control-panel',
        dataCy: 'sidebar-control-panel',
        icon: <ControlPanelIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.unactionedActivity'),
        to: isFullyManagedAgencyAndAgencyDoctor
          ? '#unactionedCritical-activity'
          : '#unactioned-activity',
        dataCy: 'sidebar-unactioned-activity',
        icon: <UnactionedIcon width={26} height={26} />,
        getColor: isFullyManagedAgencyAndAgencyDoctor
          ? getCriticalIndicatorColor
          : getIndicatorColor,
        render: true,
        marked: true,
      },
      ...(isFullyManagedAgencyAndAgencyDoctor
        ? [
            {
              key: makeid(5),
              title: t('labels.unactionedActivityNormalRisk'),
              to: '#unactionedNormalRisk-activity',
              dataCy: 'sidebar-normal-risk-unactioned-activity',
              icon: <UnactionedIcon width={26} height={26} />,
              getColor: getNormalAndAtRiskIndicatorColor,
              render: true,
              marked: true,
            },
          ]
        : []),
      {
        key: makeid(5),
        title: t('labels.actionedActivity'),
        to: '#actioned-activity',
        dataCy: 'sidebar-actioned-activity',
        icon: <ActionedIcon width={26} height={26} />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.average'),
        to: '#average-metrics',
        dataCy: 'sidebar-average-metrics',
        icon: <AverageIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.medicalHistory'),
        to: '#medical-history',
        dataCy: 'sidebar-medical-history',
        icon: <MedicalHistoryCombineIcon width={26} height={26} />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.clinicalActivity'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        icon: <ClinicalActivityCombineIcon width={26} height={26} />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        icon: <MedicationIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.services'),
        to: '#services',
        dataCy: 'sidebar-services',
        icon: <ServicesIcon width={26} height={26} />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.conditions'),
        to: '#conditions',
        dataCy: 'sidebar-conditions',
        icon: <IcdCodeIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        icon: <BannerProfileIcon />,
        render: true,
      },
      {
        key: makeid(5),
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        icon: <GeneralCommentsIcon />,
        render: !isPatient(userType),
      },
    ],
  } as const;

  return { linksList, generateLinksWithIndicators };
};
